import { useCallback } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';

import { useRestaurantsService } from '@restworld/data-services';
import { Restaurant } from '@restworld/utility-types';

import { activeRestaurantOnboardingStateAtom } from '.';
import { activeAddressIdAtom, activeRestaurantAtom } from './google-address-input-result';

export const useRestaurantInformationOnboardingQueryKey = () => {
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const activeRestaurant = useAtomValue(activeRestaurantAtom);
  return [`restaurant-onboarding-data-${activeAddressId}-${activeRestaurant}`];
};
export const restaurantInformationDataAtom = atom<Partial<Restaurant>>({});

const useRestaurantInformationOnboardingDataQuery = () => {
  const restaurantServices = useRestaurantsService();
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const activeRestaurant = useAtomValue(activeRestaurantAtom);
  const setRestaurantInformationData = useSetAtom(restaurantInformationDataAtom);
  const queryKey = useRestaurantInformationOnboardingQueryKey();

  const queryFn = useCallback(async () => {
    const data = (
      await restaurantServices.getAdditionalRestaurantOnboardingData({
        restaurant: activeRestaurant,
        gmaps_place_id: activeAddressId,
      })
    ).data;
    setRestaurantInformationData(data);
    setActiveUI('restaurant_onboarding_form');
    return data;
  }, [activeAddressId, activeRestaurant, restaurantServices, setActiveUI, setRestaurantInformationData]);

  const query = useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    retry(failureCount, error) {
      console.error(`Error fetching restaurant onboarding data: ${error}`);
      if (error?.['code'] === 409) {
        setActiveUI('restaurant_already_exists');
        return false;
      }
      if (error?.['code'] === 400 && error?.['data']?.error === 'not_horeca') {
        setActiveUI('not_horeca');
        return false;
      }
      return failureCount < 5; // Retry up to 5 times
    },
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
    enabled: !!activeAddressId && !!activeRestaurant,
  });

  return query;
};

export const useRestaurantOnboardingData = () => {
  const restaurantInformationData = useAtomValue(restaurantInformationDataAtom);
  return restaurantInformationData;
};

export default useRestaurantInformationOnboardingDataQuery;
